import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { MarketingShell } from "../components/MarketingShell.js";
import { TrapDemo } from "../components/TrapDemo.js";
import { loadSession } from "../lib/session.js";
import { FAQ_ITEMS } from "../lib/faq.js";
import { CORPUS_TOTAL_LABEL } from "../lib/corpus-label.js";

/**
 * Public marketing landing page at /.
 *
 * The page tells one story in order: you keep missing the same kind of
 * question, here is that happening live (TrapDemo), here is how a session
 * works, here is what you can practice, here is why the questions can be
 * trusted, here are the full-length tests, here is the product, questions,
 * start. The live demo sits second on purpose: the product's whole differentiator
 * is the reveal, and describing it in prose asks the visitor to take it on faith.
 *
 * Voice rules:
 *   - No em-dashes anywhere in copy.
 *   - Plain English. Avoid the internal "calibrate / diagnostic / instrument"
 *     vocabulary. Save that for inside the app.
 *   - Imperative + declarative. No salesy AI vocabulary.
 *   - Target audience: high school students, 13+. Pleasant, understandable.
 */
export function Marketing() {
  const signedIn = !!loadSession();

  return (
    <MarketingShell>
      <Hero signedIn={signedIn} />
      <TrapDemo />
      <HowItWorks />
      <ExamCoverage />
      <WhyItWorks />
      <PracticeTestCallout />
      <ScreenshotRotator />
      <FAQ />
      <FinalCTA signedIn={signedIn} />
    </MarketingShell>
  );
}

function Section({
  children,
  background,
  spacing = "lg",
}: {
  children: ReactNode;
  background?: string;
  spacing?: "lg" | "md";
}) {
  return (
    <section
      className={`bht-section bht-section--${spacing}`}
      style={{ background: background ?? "transparent" }}
    >
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>{children}</div>
    </section>
  );
}

function Hero({ signedIn }: { signedIn: boolean }) {
  return (
    <Section spacing="lg">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gridTemplateAreas: '"copy shot" "proof shot"',
          gridTemplateRows: "min-content 1fr",
          columnGap: 56,
          rowGap: 0,
          alignItems: "start",
        }}
        className="bht-hero-grid"
      >
        <div style={{ gridArea: "copy" }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--accent-amber)",
              marginBottom: 16,
            }}
          >
            SAT · ACT · AP · Common App
          </div>
          <h1
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 48,
              fontWeight: 600,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "var(--fg-0)",
              margin: 0,
              marginBottom: 20,
            }}
            className="bht-hero-headline"
          >
            See the trap before you pick the wrong answer.
          </h1>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 18,
              lineHeight: 1.55,
              color: "var(--fg-1)",
              margin: 0,
              marginBottom: 32,
              maxWidth: 520,
            }}
          >
            Take practice that behaves like the real test, then see exactly
            why the answer you picked looked right. Five minutes at a time,
            on your phone.
          </p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
            <Link
              to={signedIn ? "/app/home" : "/try"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                minHeight: 48,
                padding: "14px 22px",
                background: "var(--accent-amber)",
                color: "var(--fg-on-accent)",
                fontFamily: "var(--font-sans)",
                fontSize: 15,
                fontWeight: 600,
                borderRadius: 6,
                textDecoration: "none",
              }}
            >
              {signedIn ? "Open your dashboard" : "Try a real question, no sign-up"}
            </Link>
            <a
              href="#how-it-works"
              style={{
                color: "var(--fg-2)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                textDecoration: "none",
              }}
            >
              See how it works
            </a>
          </div>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              color: "var(--fg-3)",
              margin: "14px 0 0",
            }}
          >
            No account needed to start. Free to use. No ads.
          </p>
        </div>
        <div style={{ gridArea: "shot", alignSelf: "center", display: "flex", justifyContent: "center" }}>
          <img
            src="/shots/reveal-reading.webp"
            alt="A grammar question in the app: the wrong answer flagged in red, the correct revision in green, and the trap named and explained below the choices."
            width="412"
            height="860"
            loading="eager"
            style={{
              width: "100%",
              maxWidth: 340,
              height: "auto",
              border: "1px solid var(--border-line)",
              borderRadius: 16,
              boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
              display: "block",
              // The capture is a full phone viewport, so its bottom edge lands
              // mid-sentence. Fading the last sliver makes the crop read as
              // deliberate instead of like a truncated image.
              maskImage: "linear-gradient(to bottom, #000 86%, transparent 100%)",
              WebkitMaskImage: "linear-gradient(to bottom, #000 86%, transparent 100%)",
            }}
          />
        </div>
        <div className="bht-hero-proof" style={{ gridArea: "proof" }}>
          {([
            [CORPUS_TOTAL_LABEL, "checked questions"],
            ["50+", "full SAT & ACT tests"],
            ["Free", "no ads, no card"],
          ] as const).map(([n, label]) => (
            <div key={label} className="bht-proof-stat">
              <div className="bht-proof-num">{n}</div>
              <div className="bht-proof-label">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function AnswerRow({
  letter,
  text,
  selected,
  correct,
  trap,
}: {
  letter: string;
  text: string;
  selected?: boolean;
  correct?: boolean;
  trap?: string;
}) {
  const bg = correct
    ? "var(--signal-green-12)"
    : selected
    ? "var(--signal-red-12)"
    : "var(--bg-2)";
  const accent = correct
    ? "var(--signal-green)"
    : selected
    ? "var(--signal-red)"
    : trap
    ? "var(--trap-wrong-scope)"
    : "var(--border-line)";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: bg,
        border: `1px solid ${accent}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 4,
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--fg-0)",
          width: 12,
        }}
      >
        {letter}
      </span>
      <span
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: 11,
          color: "var(--fg-1)",
          flex: 1,
          lineHeight: 1.45,
        }}
      >
        {text}
      </span>
    </div>
  );
}

function HowItWorks() {
  return (
    <Section background="var(--bg-1)">
      <div id="how-it-works" />
      <SectionLabel>How it works</SectionLabel>
      <SectionHeadline>Three steps. Five minutes. Free.</SectionHeadline>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 24,
          marginTop: 32,
        }}
      >
        <StepCard
          number="01"
          title="Pick what to drill."
          body="Five modules cover Reading, Math Strategy, Grammar, AP essays, and Common App essays. Sessions take about five minutes. Pick what you want to work on or let us suggest based on your weak areas."
        />
        <StepCard
          number="02"
          title="Spot the trap."
          body="Each question has one right answer and three carefully designed wrong ones. You pick an answer, mark how sure you are (low, medium, high), and submit."
        />
        <StepCard
          number="03"
          title="See the pattern."
          body="On reveal we tell you which kind of trap was at play and which evidence supported the right answer. Over time you start recognizing patterns before you pick."
        />
      </div>
    </Section>
  );
}

function StepCard({
  number,
  title,
  body,
}: {
  number: string;
  title: string;
  body: string;
}) {
  return (
    <div
      style={{
        padding: 24,
        background: "var(--bg-0)",
        border: "1px solid var(--border-line)",
        borderRadius: 8,
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          fontWeight: 600,
          color: "var(--accent-amber)",
          letterSpacing: "0.06em",
          marginBottom: 14,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {number}
      </div>
      <h3
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: 20,
          fontWeight: 600,
          color: "var(--fg-0)",
          margin: 0,
          marginBottom: 10,
        }}
      >
        {title}
      </h3>
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: 14,
          color: "var(--fg-2)",
          lineHeight: 1.6,
          margin: 0,
        }}
      >
        {body}
      </p>
    </div>
  );
}

/** Bar widths for the Home mock on the marketing page. Decorative and fixed:
 *  see the note at the point of use. */
const MOCK_BAR_WIDTHS = ["72%", "58%", "64%", "51%"];

const MODULES: Array<{
  name: string;
  exam: string;
  body: string;
  color: string;
}> = [
  {
    name: "Reading",
    exam: "SAT · ACT",
    body: "Cut through the wrong-answer traps in passage questions. Learn the seven that catch otherwise strong readers.",
    color: "var(--signal-cyan)",
  },
  {
    name: "Math Strategy",
    exam: "SAT · ACT",
    body: "Pick the fastest path to a problem: graph, table, substitution, estimate. Faster lens, fewer mistakes.",
    color: "var(--accent-amber)",
  },
  {
    name: "Grammar",
    exam: "SAT · ACT",
    body: "Spot the rule actually being tested, then pick the punctuation. Stop guessing between commas and semicolons.",
    color: "var(--signal-violet)",
  },
  {
    name: "AP Essays",
    exam: "AP",
    body: "Map your evidence to the rubric row that graders score on. Hit the specific points the rubric rewards.",
    color: "var(--trap-wrong-scope)",
  },
  {
    name: "Application Essays",
    exam: "Common App",
    body: "Decode what a Common App prompt actually asks you to show. Stop writing the essay you wish they asked for.",
    color: "#E07AD7",
  },
];

/**
 * Exam coverage, grouped so the hierarchy reads at a glance: the two tests most
 * visitors came for get the large panels, and the AP / application-essay work
 * sits below as a real but secondary lane. The previous flat five-card grid gave
 * "Application Essays" the same visual weight as "SAT Reading", which made the
 * product look unfocused rather than broad.
 */
function ExamCoverage() {
  return (
    <Section>
      <SectionLabel>What you can practice</SectionLabel>
      <SectionHeadline>What are you preparing for?</SectionHeadline>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
          gap: 20,
          marginTop: 32,
        }}
      >
        <ExamPanel
          name="Digital SAT"
          summary="Reading, Writing, and Math, with full-length tests that route to a harder or easier second module the way the real one does."
          bullets={[
            "Reading: the wrong-answer traps in passage questions",
            "Math: pick the fastest path, then avoid the wrong-question trap",
            "Grammar: spot the rule actually being tested",
            "Full-length tests scored 200 to 800 per section",
          ]}
        />
        <ExamPanel
          name="ACT"
          summary="English, Math, and Reading, plus the optional Science section, on the 2026 format and scored 1 to 36 with a composite."
          bullets={[
            "English: translate the stem, then pick the revision",
            "Math: 45 questions in 50 minutes, so pacing is the skill",
            "Reading: four passages under real time pressure",
            "Science: reading graphs and experiments, not memorized facts",
          ]}
        />
      </div>
      <div className="bht-exam-secondary">
        <div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              fontWeight: 600,
              color: "var(--fg-3)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Also here
          </div>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 14,
              color: "var(--fg-2)",
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            <strong style={{ color: "var(--fg-1)", fontWeight: 600 }}>PSAT.</strong>{" "}
            Full-length PSAT/NMSQT and PSAT 10 tests in the current digital format.
            The two are the same test, so one sitting prepares you for either.{" "}
            <strong style={{ color: "var(--fg-1)", fontWeight: 600 }}>AP essays.</strong>{" "}
            Map your evidence to the rubric row a grader actually scores, including
            AP Calculus practice.{" "}
            <strong style={{ color: "var(--fg-1)", fontWeight: 600 }}>
              Application essays.
            </strong>{" "}
            Work out what a Common App prompt is really asking you to show, before
            you write the essay you wish they had asked for.
          </p>
        </div>
      </div>
    </Section>
  );
}

function ExamPanel({
  name,
  summary,
  bullets,
}: {
  name: string;
  summary: string;
  bullets: string[];
}) {
  return (
    <div
      style={{
        padding: 26,
        background: "var(--bg-1)",
        border: "1px solid var(--border-line)",
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <h3
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: 22,
          fontWeight: 600,
          color: "var(--fg-0)",
          margin: 0,
        }}
      >
        {name}
      </h3>
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: 14,
          color: "var(--fg-2)",
          lineHeight: 1.6,
          margin: 0,
        }}
      >
        {summary}
      </p>
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "grid",
          gap: 8,
          borderTop: "1px solid var(--border-line)",
          paddingTop: 14,
        }}
      >
        {bullets.map((b) => (
          <li
            key={b}
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              color: "var(--fg-1)",
              lineHeight: 1.5,
              display: "flex",
              gap: 10,
            }}
          >
            <span style={{ color: "var(--accent-amber)", flexShrink: 0 }}>·</span>
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Merged trust section. This used to be two sections (WhyDifferent +
 * QualityProof) that both carried a near-verbatim "every question is solved from
 * scratch and independently checked" pillar, which read as padding. One section
 * makes the argument once, then shows the process instead of restating it.
 */
function WhyItWorks() {
  return (
    <Section background="var(--bg-1)">
      <SectionLabel>Why it works</SectionLabel>
      <SectionHeadline>Built for accuracy, not for a bigger number.</SectionHeadline>
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: 16,
          color: "var(--fg-2)",
          lineHeight: 1.6,
          margin: "16px 0 0",
          maxWidth: 640,
        }}
      >
        A big practice library is worth nothing if an answer key is wrong or a
        question does not behave like the real test. That is the fastest way a
        prep tool loses you, so we built against it.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 24,
          marginTop: 32,
        }}
      >
        <PillarCard
          eyebrow="Patterns, not answers"
          body="An app can hand you the answer to one question. That does nothing on test day, when it is just you and four choices. We show you why the wrong answers are built to look right, so you catch them on your own."
        />
        <PillarCard
          eyebrow="Answer keys you can trust"
          body="Every question is solved from scratch, independently, before it ships, and the answer keys are hidden from whoever solves them. If a solver disagrees with the key, nothing ships until a person settles it."
        />
        <PillarCard
          eyebrow="Honest scoring"
          body="Full practice tests are scored on the College Board's published conversion (200 to 800 per section) and the ACT's 1 to 36 scale. The conversion is official, the questions are ours, so read the number as a solid estimate."
        />
      </div>

      <div className="bht-process">
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 600,
            color: "var(--fg-2)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            marginBottom: 18,
          }}
        >
          How a question ships
        </div>
        <ol className="bht-process-list">
          {[
            "Written to one named skill and one specific trap on the current exam",
            "Math and figures verified against the numbers, option by option",
            "Solved blind by independent solvers who cannot see the key",
            "Reading answers must be supported by the passage on your screen",
            "Duplicates and re-skinned questions rejected",
            "Flag it in one tap and a person reviews it",
          ].map((step, i) => (
            <li key={step}>
              <span className="bht-process-num">{String(i + 1).padStart(2, "0")}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
        <Link
          to="/methodology"
          style={{
            display: "inline-block",
            marginTop: 20,
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--accent-amber)",
            textDecoration: "none",
          }}
        >
          Read the full methodology
        </Link>
      </div>
    </Section>
  );
}

function PillarCard({ eyebrow, body }: { eyebrow: string; body: string }) {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          fontWeight: 600,
          color: "var(--accent-amber)",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          marginBottom: 12,
        }}
      >
        {eyebrow}
      </div>
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: 15,
          color: "var(--fg-1)",
          lineHeight: 1.6,
          margin: 0,
        }}
      >
        {body}
      </p>
    </div>
  );
}

function PracticeTestCallout() {
  return (
    <Section>
      <div
        style={{
          padding: "40px 32px",
          background: "var(--bg-1)",
          border: "1px solid var(--border-line)",
          borderRadius: 12,
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 32,
          alignItems: "center",
        }}
        className="bht-callout-grid"
      >
        <div>
          <SectionLabel>Out of official practice tests? Start here</SectionLabel>
          <h2
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 28,
              fontWeight: 600,
              color: "var(--fg-0)",
              margin: 0,
              marginTop: 12,
              marginBottom: 10,
              lineHeight: 1.25,
            }}
          >
            50+ full-length tests for when you've run out of Bluebook.
          </h2>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 15,
              color: "var(--fg-2)",
              lineHeight: 1.6,
              margin: 0,
              maxWidth: 580,
            }}
          >
            98 questions, two hours, Reading &amp; Writing plus Math, structured
            the same way as the real digital SAT, including the harder second
            module that catches people off guard. You get a 200 to 800 scaled
            score per section using the College Board's published conversion,
            plus the skills that tripped you up the most. New to the test? A
            short placement gives you a rough starting range in a few minutes first.
          </p>
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 88,
            fontWeight: 600,
            color: "var(--accent-amber)",
            lineHeight: 1,
            letterSpacing: "-0.04em",
            fontVariantNumeric: "tabular-nums",
            textAlign: "center",
          }}
          className="bht-callout-numeral"
        >
          1600
        </div>
      </div>
    </Section>
  );
}

/**
 * Product panels behind a manual tab list.
 *
 * Deliberately NOT auto-advancing: a panel that changes under the reader every
 * five seconds is a carousel, and it competes with the live demo above for
 * attention. The reveal panel also came out, because the visitor has already
 * done the real thing in TrapDemo and the hero shows a real screenshot of it.
 */
function ScreenshotRotator() {
  const SHOTS = [
    {
      label: "Daily home",
      caption: "Hero numerals, last 7 days, modules grid.",
      body: <MockHome />,
    },
    {
      label: "Drill loop",
      caption: "Sticky timer at top, four choices, confidence toggle.",
      body: <MockDrill />,
    },
    {
      label: "Practice test report",
      caption: "Scaled score, per section, top missed skills with drill links.",
      body: <MockReport />,
    },
  ];

  const [active, setActive] = useState(0);
  const current = SHOTS[active]!;

  return (
    <Section background="var(--bg-1)">
      <SectionLabel>The product</SectionLabel>
      <SectionHeadline>What you'll actually use.</SectionHeadline>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)",
          gap: 40,
          marginTop: 32,
          alignItems: "start",
        }}
        className="bht-rotator-grid"
      >
        <div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
            {SHOTS.map((s, i) => (
              <li key={s.label}>
                <button
                  type="button"
                  onClick={() => setActive(i)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 14px",
                    background: i === active ? "var(--bg-0)" : "transparent",
                    border: i === active
                      ? "1px solid var(--border-strong)"
                      : "1px solid var(--border-line)",
                    borderLeft: i === active
                      ? "3px solid var(--accent-amber)"
                      : "3px solid transparent",
                    borderRadius: 4,
                    cursor: "pointer",
                    color: "inherit",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      fontWeight: 600,
                      color: i === active ? "var(--accent-amber)" : "var(--fg-2)",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontSize: 14,
                      fontWeight: 600,
                      color: i === active ? "var(--fg-0)" : "var(--fg-1)",
                      marginBottom: 2,
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontSize: 12,
                      color: "var(--fg-3)",
                      lineHeight: 1.45,
                    }}
                  >
                    {s.caption}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div
          style={{
            padding: 16,
            background: "var(--bg-0)",
            border: "1px solid var(--border-line)",
            borderRadius: 10,
            minHeight: 360,
            boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
          }}
        >
          {current.body}
        </div>
      </div>
    </Section>
  );
}

function MockHome() {
  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div style={{ display: "flex", gap: 28 }}>
        <Numeral value="142" label="Attempts" color="var(--accent-amber)" size={40} />
        <Numeral value="76%" label="Accuracy" color="var(--fg-0)" size={32} />
        <Numeral value="2.1s" label="Reaction · median" color="var(--fg-0)" size={32} />
      </div>
      <div>
        <Eyebrow>Last 7 days</Eyebrow>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginTop: 8 }}>
          {[
            ["S", "21", 4],
            ["M", "22", 8],
            ["T", "23", 0],
            ["W", "24", 12],
            ["T", "25", 6],
            ["F", "26", 0],
            ["S", "27", 9],
          ].map(([d, mm, n], i) => (
            <DayPill key={i} d={d as string} mm={mm as string} n={n as number} isToday={i === 6} />
          ))}
        </div>
      </div>
      <div>
        <Eyebrow>Choose a subject</Eyebrow>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginTop: 8,
          }}
        >
          {MODULES.slice(0, 4).map((m, i) => (
            <div
              key={m.name}
              style={{
                padding: 10,
                background: "var(--bg-1)",
                border: "1px solid var(--border-line)",
                borderRadius: 5,
                position: "relative",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 9,
                  color: m.color,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                {m.name}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--fg-3)",
                  marginBottom: 6,
                }}
              >
                {m.exam}
              </div>
              <div style={{ height: 2, background: "var(--bg-3)", borderRadius: 1 }}>
                <div
                  style={{
                    height: "100%",
                    // FIXED, not Math.random(). This sat inside a prerendered
                    // mock, so it baked a different width into index.html on
                    // every build (67% one run, 56% the next) and then rendered
                    // a third value on hydration. Three consequences: the build
                    // was not reproducible, React hydrated a mismatch on the
                    // public landing page, and a filled bar under a subject
                    // name reads as a real figure when it is decoration. Every
                    // other number in this mock is a literal; so is this one.
                    width: MOCK_BAR_WIDTHS[i % MOCK_BAR_WIDTHS.length],
                    background: m.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MockDrill() {
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          padding: "8px 0",
          borderBottom: "1px solid var(--border-line)",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            fontWeight: 600,
            color: "var(--accent-amber)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            padding: "3px 6px",
            border: "1px solid var(--accent-amber)",
            borderRadius: 3,
          }}
        >
          READING
        </span>
        <Eyebrow>Item 008 / 100</Eyebrow>
        <div style={{ flex: 1, height: 3, background: "var(--bg-3)", borderRadius: 2 }}>
          <div style={{ width: "8%", height: "100%", background: "var(--accent-amber)" }} />
        </div>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-0)" }}>1.42s</span>
      </div>
      <div>
        <Eyebrow>Passage</Eyebrow>
        <p style={{ fontFamily: "var(--font-sans)", fontSize: 12, color: "var(--fg-1)", lineHeight: 1.5, marginTop: 6 }}>
          The author's argument rests on a contrast between rigorous
          theory and improvisational practice. Yet the example offered
          in the third paragraph quietly undermines that contrast.
        </p>
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        <AnswerRow letter="A" text="Restates the main claim." />
        <AnswerRow letter="B" text="Provides a counterexample." selected />
        <AnswerRow letter="C" text="Cites supporting evidence." />
        <AnswerRow letter="D" text="Signals a transition." />
      </div>
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          paddingTop: 10,
          borderTop: "1px solid var(--border-line)",
        }}
      >
        <Eyebrow>Confidence</Eyebrow>
        {["LOW", "MED", "HIGH"].map((c, i) => (
          <span
            key={c}
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              padding: "3px 8px",
              border: `1px solid ${i === 1 ? "var(--accent-amber)" : "var(--border-line)"}`,
              color: i === 1 ? "var(--accent-amber)" : "var(--fg-2)",
              borderRadius: 3,
              fontWeight: 600,
              letterSpacing: "0.08em",
            }}
          >
            {c}
          </span>
        ))}
      </div>
    </div>
  );
}

function MockReport() {
  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div>
        <Eyebrow>Practice test · Form A</Eyebrow>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 72,
            fontWeight: 600,
            color: "var(--fg-0)",
            lineHeight: 1,
            letterSpacing: "-0.04em",
            marginTop: 8,
          }}
        >
          1410
        </div>
        <div
          style={{
            color: "var(--fg-3)",
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            marginTop: 6,
          }}
        >
          estimated SAT-style score, out of 1600 · plus or minus 60 points
        </div>
      </div>
      <div
        style={{
          padding: 14,
          background: "var(--bg-1)",
          border: "1px solid var(--border-line)",
          borderRadius: 6,
        }}
      >
        <Eyebrow>Per section</Eyebrow>
        <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
          <SectionLine label="Reading & Writing" scaled={720} pct={84} color="var(--signal-green)" />
          <SectionLine label="Math" scaled={690} pct={78} color="var(--accent-amber)" />
        </div>
      </div>
    </div>
  );
}

function SectionLine({
  label,
  scaled,
  pct,
  color,
}: {
  label: string;
  scaled: number;
  pct: number;
  color: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 500, color: "var(--fg-0)" }}>{label}</div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-3)", marginTop: 2 }}>
          {pct}% correct
        </div>
      </div>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 24,
          fontWeight: 600,
          color,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {scaled}
      </span>
    </div>
  );
}

function Numeral({
  value,
  label,
  color,
  size,
}: {
  value: string;
  label: string;
  color: string;
  size: number;
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: size,
          fontWeight: 600,
          lineHeight: 1,
          color,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>
      <Eyebrow>{label}</Eyebrow>
    </div>
  );
}

function DayPill({ d, mm, n, isToday }: { d: string; mm: string; n: number; isToday: boolean }) {
  const dotColor =
    n === 0 ? "var(--border-line)" : n < 3 ? "var(--accent-amber-dim)" : n < 10 ? "var(--accent-amber)" : "var(--signal-green)";
  return (
    <div
      style={{
        padding: "6px 2px",
        background: isToday ? "var(--bg-2)" : "var(--bg-1)",
        border: `1px solid ${isToday ? "var(--border-strong)" : "var(--border-line)"}`,
        borderRadius: 4,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--fg-3)", letterSpacing: "0.08em" }}>{d}</div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-1)", fontWeight: 600 }}>{mm}</div>
      <div style={{ width: 5, height: 5, borderRadius: "50%", background: dotColor }} />
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--fg-2)" }}>{n || "·"}</div>
    </div>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 9,
        fontWeight: 600,
        color: "var(--fg-2)",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

/**
 * Short FAQ as a native accordion.
 *
 * This used to print every one of the ~17 shared FAQ entries in full, which
 * ended the page on a wall of text and let the FAQ carry the sales argument.
 * Now the landing page shows the six a first-time visitor actually asks, folded
 * shut, and /faq keeps the complete list. <details> is used deliberately over a
 * custom disclosure: it is keyboard and screen-reader correct for free, and the
 * answers stay in the DOM for crawlers even while collapsed.
 */
function FAQ() {
  const items = FAQ_ITEMS.filter((it) => it.landing);
  return (
    <Section>
      <SectionLabel>Questions</SectionLabel>
      <SectionHeadline>What students usually ask.</SectionHeadline>
      <div className="bht-faq-list">
        {items.map((it) => (
          <details key={it.q} className="bht-faq-item">
            <summary>
              <span>{it.q}</span>
              <span className="bht-faq-chevron" aria-hidden="true" />
            </summary>
            <p>{it.a}</p>
          </details>
        ))}
      </div>
      <Link
        to="/faq"
        style={{
          display: "inline-block",
          marginTop: 24,
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "var(--accent-amber)",
          textDecoration: "none",
        }}
      >
        All questions
      </Link>
    </Section>
  );
}

function FinalCTA({ signedIn }: { signedIn: boolean }) {
  return (
    <Section background="var(--bg-1)">
      <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto" }}>
        <SectionLabel center>Ready to start</SectionLabel>
        <h2
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 36,
            fontWeight: 600,
            color: "var(--fg-0)",
            margin: 0,
            marginTop: 16,
            marginBottom: 16,
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
          }}
          className="bht-cta-headline"
        >
          Five minutes, your phone, one question at a time.
        </h2>
        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 16,
            color: "var(--fg-1)",
            lineHeight: 1.55,
            margin: 0,
            marginBottom: 28,
          }}
        >
          Free to use. Sign in with Google to set your target exam and pick
          your first module.
        </p>
        <Link
          to={signedIn ? "/app/home" : "/signin"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "16px 28px",
            background: "var(--accent-amber)",
            color: "var(--fg-on-accent)",
            fontFamily: "var(--font-sans)",
            fontSize: 16,
            fontWeight: 600,
            borderRadius: 6,
            textDecoration: "none",
          }}
        >
          {signedIn ? "Open your dashboard" : "Start practicing"}
        </Link>
      </div>
    </Section>
  );
}

function SectionLabel({ children, center }: { children: ReactNode; center?: boolean }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        fontWeight: 600,
        color: "var(--accent-amber)",
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        marginBottom: 12,
        textAlign: center ? "center" : "left",
      }}
    >
      {children}
    </div>
  );
}

function SectionHeadline({ children }: { children: ReactNode }) {
  return (
    <h2
      style={{
        fontFamily: "var(--font-sans)",
        fontSize: 32,
        fontWeight: 600,
        color: "var(--fg-0)",
        margin: 0,
        lineHeight: 1.2,
        letterSpacing: "-0.015em",
      }}
      className="bht-section-headline"
    >
      {children}
    </h2>
  );
}
